import React, { useState, useContext } from "react";
import {
	Box,
	Typography,
	Button,
	List,
	ListItem,
	IconButton,
	getLuminance,
} from "@mui/material";
import { format, addDays, startOfWeek, addWeeks, subWeeks } from "date-fns";
import { es } from "date-fns/locale"; // Importar español

import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { CreatorContext } from "../../contexts/CreatorContext";
import { useTranslation } from "react-i18next";

function CustomCalendar({ onDateTimeSelect, onTimeSelect, availability }) {
	// Establecemos la fecha de hoy como fecha seleccionada inicial
	const today = new Date();
	const [selectedDate, setSelectedDate] = useState(today);
	const [selectedTime, setSelectedTime] = useState(null);
	const [currentWeek, setCurrentWeek] = useState(
		startOfWeek(today, { weekStartsOn: 1 })
	); // Semana actual

	// Generar los días de la semana actual
	const daysOfWeek = Array.from({ length: 7 }).map((_, index) =>
		addDays(currentWeek, index)
	);
	const { t } = useTranslation();

	// Obtener las horas disponibles para el día seleccionado
	const formattedDate = selectedDate
		? format(selectedDate, "yyyy-MM-dd")
		: null;
	const availableTimes =
		formattedDate && availability?.[formattedDate]
			? availability[formattedDate]
			: [];

	const { creator } = useContext(CreatorContext);

	// Función para cambiar de semana
	const handleNextWeek = () => {
		setCurrentWeek(addWeeks(currentWeek, 1));
		setSelectedDate(null); // Reseteamos la fecha seleccionada al cambiar de semana
		setSelectedTime(null); // Reseteamos la hora seleccionada al cambiar de semana
	};

	const handlePreviousWeek = () => {
		setCurrentWeek(subWeeks(currentWeek, 1));
		setSelectedDate(null);
		setSelectedTime(null);
	};

	const handleDateChange = (date) => {
		console.log("Date selected:", date);
		setSelectedDate(date);
		setSelectedTime(null); // Reset time on date change

		// Trigger the date selection for available hours
		if (onDateTimeSelect) {
			onDateTimeSelect({ date });
		}
	};

	const handleTimeSelect = (time) => {
		setSelectedTime(time);
		if (onTimeSelect) {
			onTimeSelect({ time });
		}
	};

	return (
		<Box
			sx={{
				width: "100%",
				marginBottom: { md: "84px", lg: "20px" },
				/*maxWidth: "400px" */
			}}
		>
			{/* Botones para navegar entre semanas */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					marginBottom: 2,
					color: creator?.fontColor ?? "#000000",
				}}
			>
				<IconButton onClick={handlePreviousWeek}>
					<ArrowBackIosIcon
						sx={{ fontSize: "14px", color: creator?.fontColor ?? "#000000" }}
					/>
				</IconButton>
				<Typography sx={{ fontSize: "16px" }} variant="h6">
					{format(currentWeek, "MMMM yyyy", { locale: es })}{" "}
					{/* Mostrar mes en español */}
				</Typography>
				<IconButton onClick={handleNextWeek}>
					<ArrowForwardIosIcon
						sx={{ fontSize: "14px", color: creator?.fontColor ?? "#000000" }}
					/>
				</IconButton>
			</Box>

			{/* Visualización de los días de la semana */}
			<Box
				sx={{
					display: "flex",
					justifyContent: { xs: "center", sm: "space-between" },
				}}
			>
				{daysOfWeek.map((day) => {
					const formattedDay = format(day, "yyyy-MM-dd");
					const isPastDay =
						day.setHours(0, 0, 0, 0) < today.setHours(0, 0, 0, 0);

					return (
						<Button
							key={day}
							variant="text"
							onClick={() => handleDateChange(day)}
							disabled={isPastDay}
							disableRipple // Desactiva el efecto de "ripple" al hacer clic
							sx={{
								padding: "10px",
								minWidth: "40px",
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								borderRadius: "50%", // Hace el botón redondeado
								opacity: isPastDay ? 0.5 : 1,
							}}
						>
							<Typography
								variant="caption"
								sx={{ color: creator?.fontColor ?? "#000000" }} // El día de la semana permanece siempre negro
							>
								{format(day, "EEE", { locale: es })}{" "}
								{/* Día de la semana en español */}
							</Typography>

							<Typography
								variant="h6"
								sx={{
									fontSize: "16px", // Tamaño de fuente más pequeño
									color:
										formattedDay === formattedDate
											? getLuminance(creator?.mainColor || "#000000") >= 0.5
												? "#000000"
												: "#FFFFFF"
											: creator?.fontColor ?? "#000000", // Texto blanco si está seleccionado, negro si no
									backgroundColor:
										formattedDay === formattedDate
											? creator?.mainColor || "#43BD98"
											: "transparent", // Fondo solo para el número del día seleccionado
									width: "32px", // Asegura que sea un círculo
									height: "32px", // Altura igual al ancho para hacerlo circular
									lineHeight: "32px", // Centra verticalmente el número
									borderRadius: "50%", // Borde redondeado
									textAlign: "center", // Centra el número horizontalmente
								}}
							>
								{format(day, "dd", { locale: es })} {/* Día del mes */}
							</Typography>
						</Button>
					);
				})}
			</Box>

			{/* Lista de horarios disponibles */}
			{selectedDate ? (
				availableTimes.length > 0 ? (
					<Box
						sx={{
							flex: "1 1 auto",
							overflowY: "auto",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "flex-start",
							marginTop: "10px",
							paddingBottom: "100px",
							maxHeight: "calc(100vh - 300px)", // puedes ajustar este número
							WebkitOverflowScrolling: "touch",
							"&::-webkit-scrollbar": { width: 0 }, // opcional: oculta scrollbar
						}}
					>
						<List
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								width: "100%",
								justifyContent: "flex-start",
								paddingBottom: "120px",
							}}
						>
							{availableTimes.map((time) => (
								<ListItem
									key={time}
									sx={{
										padding: "12px",
										cursor: "pointer",
										textAlign: "center",
										display: "block",
										justifyContent: "space-between",
										borderBottom: "1px solid #00000007",

										color:
											selectedTime === time
												? creator?.mainColor || "#43BD98"
												: creator?.fontColor ?? "#000000",
										"&:hover": { backgroundColor: "#00000007" }, // Cambia el fondo en hover
									}}
									onClick={() => handleTimeSelect(time)}
								>
									<Typography>{time}</Typography>
								</ListItem>
							))}
						</List>
					</Box>
				) : (
					<Typography
						sx={{ marginTop: "8px", marginBottom: 2, textAlign: "center" }}
					>
						{t("noHours")}
					</Typography>
				)
			) : (
				<Typography sx={{ marginTop: 8, textAlign: "center", marginBottom: 2 }}>
					{t("selectHour")}
				</Typography>
			)}
		</Box>
	);
}

export default CustomCalendar;
